import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Flag, Loader2, Lock, ShieldCheck, Sparkles, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { Mascot } from "@/components/assistant/mascot";
import { PLANS_ROUTE } from "@/components/document-limit";
import { adjustMine, adjustModel, fetchAiStatus, type AdjustResult, type AiStatus } from "@/lib/library-client";

export type AiAdjustTarget = { kind: "model"; slug: string } | { kind: "mine"; id: string };

const TEXTS = {
  pt: {
    title: "Ajustar com IA",
    lead: "Diga com suas palavras o que mudar. A IA escreve uma cópia ajustada deste modelo em Meus modelos.",
    private: (days: number) => `Só você vê a cópia. Ela fica ${days} dias em Meus modelos e depois é apagada.`,
    placeholder: "Ex.: incluir multa de 10% por atraso no pagamento do aluguel",
    ideas: "Ideias",
    chips: [
      { label: "Traduzir para inglês", text: "Traduzir o contrato inteiro para inglês", en: false },
      { label: "Voltar para português", text: "Traduzir o contrato de volta para o português", en: true },
      { label: "Linguagem mais simples", text: "Deixar a linguagem mais simples e fácil de entender, sem mudar o que foi combinado" },
      { label: "Multa por atraso", text: "Incluir multa de 2% e juros de 1% ao mês em caso de atraso no pagamento" },
      { label: "Confidencialidade", text: "Incluir uma cláusula de confidencialidade" },
      { label: "Parte empresa (CNPJ)", text: "Adaptar para que uma das partes seja empresa, com razão social, CNPJ e representante legal" },
    ],
    meter: (used: number, limit: number, resets: string) => `${used} de ${limit} ajustes usados este mês · renova em ${resets}`,
    unlimited: "Sem limite (administrador)",
    brazil: "Modelos brasileiros, de acordo com a lei do Brasil. Traduzir para inglês muda só o idioma: o contrato continua regido pela lei brasileira.",
    privacy: "A IA lê só o texto do modelo, nunca os dados que você preencheu.",
    charge: "Só conta no limite quando o ajuste dá certo. Revise o resultado antes de enviar para assinatura.",
    submit: "Ajustar com IA",
    cancel: "Cancelar",
    close: "Fechar",
    running: "Ajustando… costuma levar de 10 a 30 segundos. Traduzir o contrato inteiro leva 1 a 2 minutos.",
    runningLeave: "Pode fechar esta janela: quando terminar, a cópia aparece em Meus modelos.",
    lockedTitle: "Ajustar com IA é dos planos pagos",
    lockedText: "Peça mudanças com suas palavras (incluir uma cláusula, simplificar a linguagem, traduzir para inglês) e a IA escreve a cópia para você.",
    lockedFree: "No plano grátis você usa os modelos e preenche os campos normalmente.",
    plans: "Ver planos",
    limitReached: (limit: number, resets: string) => `Você usou os ${limit} ajustes com IA deste mês. Renova em ${resets}.`,
    tooLarge: "Este modelo é grande demais para o Ajustar com IA. Use o Texto livre para mudar o que precisar.",
    disabled: "O ajuste com IA está desligado no momento.",
    failed: "Não foi possível ajustar agora.",
    failedTitle: "Ajuste com IA",
    readyTitle: "Ajuste pronto",
    readyText: "A cópia ajustada está em Meus modelos.",
    open: "Abrir",
    count: (n: number, max: number) => `${n}/${max}`,
  },
  en: {
    title: "Adjust with AI",
    lead: "Say in your own words what to change. The AI writes an adjusted copy of this model into My templates.",
    private: (days: number) => `Only you can see the copy. It stays in My templates for ${days} days and is then deleted.`,
    placeholder: "E.g. add a 10% late fee if the rent is paid late",
    ideas: "Ideas",
    chips: [
      { label: "Translate to English", text: "Translate the whole contract into English", en: false },
      { label: "Back to Portuguese", text: "Translate the contract back into Portuguese", en: true },
      { label: "Simpler language", text: "Make the language simpler and easier to understand, without changing what was agreed" },
      { label: "Late fee", text: "Add a 2% fine plus 1% interest per month for late payment" },
      { label: "Confidentiality", text: "Add a confidentiality clause" },
      { label: "Company party (CNPJ)", text: "Adapt it so one of the parties is a company, with corporate name, CNPJ and legal representative" },
    ],
    meter: (used: number, limit: number, resets: string) => `${used} of ${limit} adjustments used this month · renews on ${resets}`,
    unlimited: "Unlimited (administrator)",
    brazil: "Brazilian models, under Brazilian law. Translating into English changes only the language: the contract is still governed by Brazilian law.",
    privacy: "The AI reads only the model's text, never the data you filled in.",
    charge: "It only counts toward your limit when the adjustment works. Review the result before sending it for signature.",
    submit: "Adjust with AI",
    cancel: "Cancel",
    close: "Close",
    running: "Adjusting… it usually takes 10 to 30 seconds. Translating the whole contract takes 1 to 2 minutes.",
    runningLeave: "You can close this window: when it is done, the copy shows up in My templates.",
    lockedTitle: "Adjust with AI is part of the paid plans",
    lockedText: "Ask for changes in your own words (add a clause, simplify the language, translate into English) and the AI writes the copy for you.",
    lockedFree: "On the free plan you use the models and fill in the fields as usual.",
    plans: "See plans",
    limitReached: (limit: number, resets: string) => `You have used your ${limit} AI adjustments this month. They renew on ${resets}.`,
    tooLarge: "This model is too large for AI adjustment. Use Free text to change what you need.",
    disabled: "AI adjustment is turned off at the moment.",
    failed: "Could not adjust right now.",
    failedTitle: "Adjust with AI",
    readyTitle: "Adjustment ready",
    readyText: "The adjusted copy is in My templates.",
    open: "Open",
    count: (n: number, max: number) => `${n}/${max}`,
  },
};

/** "01/11" in Brasília time (the monthly quota renews at midnight there). */
export function formatResetDate(iso: string, language: string): string {
  try {
    return new Intl.DateTimeFormat(language === "en" ? "en-US" : "pt-BR", { day: "2-digit", month: "2-digit", timeZone: "America/Sao_Paulo" }).format(new Date(iso));
  } catch {
    return "";
  }
}

export function useAiStatus(enabled = true) {
  return useQuery({ queryKey: ["library-ai-status"], queryFn: fetchAiStatus, staleTime: 30_000, enabled });
}

/** What the page shows on the "Ajustar com IA" button: hidden when the feature is off. */
export function aiButtonState(status: AiStatus | undefined): "hidden" | "locked" | "ready" {
  if (!status || !status.enabled) return "hidden";
  return status.allowed ? "ready" : "locked";
}

interface AiAdjustDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: AiAdjustTarget;
  /** Language of the document being adjusted ("en" for a translated copy). */
  docLang: string;
  /** Length of the template HTML: bigger than the API limit means the request is refused up front. */
  docChars: number;
  /** Text arriving from the chat ("Usar e ajustar com IA"). */
  initialInstruction?: string | null;
  /** Called when the adjustment worked and the page is still open. */
  onDone: (result: AdjustResult) => void;
}

export function AiAdjustDialog({ open, onOpenChange, target, docLang, docChars, initialInstruction, onDone }: AiAdjustDialogProps) {
  const { language } = useLanguage();
  const t = language === "en" ? TEXTS.en : TEXTS.pt;
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const status = useAiStatus();
  const s = status.data;
  const max = s?.maxInstructionLength || 500;

  const [instruction, setInstruction] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (open && initialInstruction) setInstruction(initialInstruction.slice(0, max));
    if (open) { setError(null); void status.refetch(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialInstruction]);

  const resets = s ? formatResetDate(s.resetsAt, language) : "";
  const locked = !!s && !s.allowed;
  const limitReached = !!s && s.allowed && !s.unlimited && s.remaining <= 0;
  const tooLarge = !!s && docChars > s.maxTemplateChars;
  const blocked = !s || !s.enabled || locked || limitReached || tooLarge;
  const text = instruction.trim();

  const submit = async () => {
    if (blocked || running || !text) return;
    setRunning(true);
    setError(null);
    try {
      const r = target.kind === "model" ? await adjustModel(target.slug, text, language) : await adjustMine(target.id, text, language);
      queryClient.setQueryData(["library-ai-status"], r.status);
      queryClient.setQueryData(["library-mine", r.template.id], r.template);
      void queryClient.invalidateQueries({ queryKey: ["library-mine-list"] });
      if (mounted.current) {
        setInstruction("");
        onOpenChange(false);
        onDone(r);
      } else {
        // the person left the page while the AI worked: the copy is saved, point to it
        toast({
          title: t.readyTitle,
          description: t.readyText,
          action: <ToastAction altText={t.open} onClick={() => navigate(`/modelos/meus/${r.template.id}`)}>{t.open}</ToastAction>,
        });
      }
    } catch (err: any) {
      const body = err?.body;
      if (body?.status) queryClient.setQueryData(["library-ai-status"], body.status);
      const message = (language === "en" ? body?.messageEn : body?.messagePt) || t.failed;
      if (mounted.current) setError(message);
      if (!mounted.current || !openRef.current) toast({ title: t.failedTitle, description: message, variant: "destructive" });
    } finally {
      if (mounted.current) setRunning(false);
    }
  };

  const chips = t.chips.filter((c) => c.en === undefined || c.en === (docLang === "en"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-violet-600" /> {t.title}
          </DialogTitle>
          <DialogDescription>{t.lead}</DialogDescription>
        </DialogHeader>

        {status.isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> …</div>}

        {s && locked && (
          <div className="space-y-3 rounded-xl border border-violet-200 bg-violet-50 p-4 dark:border-violet-900 dark:bg-violet-950/30" data-testid="ai-locked">
            <p className="flex items-center gap-2 font-medium"><Lock className="h-4 w-4 text-violet-700 dark:text-violet-300" /> {t.lockedTitle}</p>
            <p className="text-sm">{t.lockedText}</p>
            <p className="text-sm text-muted-foreground">{t.lockedFree}</p>
            <Button className="gap-2" onClick={() => { onOpenChange(false); navigate(PLANS_ROUTE); }} data-testid="ai-plans">
              <Sparkles className="h-4 w-4" /> {t.plans}
            </Button>
          </div>
        )}

        {s && !s.enabled && <p className="rounded-xl border bg-muted/40 p-3 text-sm" data-testid="ai-disabled">{t.disabled}</p>}

        {s && s.enabled && !locked && (
          <>
            <div className="flex items-center justify-between gap-2 rounded-xl border bg-muted/30 px-3 py-2 text-xs" data-testid="ai-meter">
              <span>{s.unlimited ? t.unlimited : t.meter(s.used, s.limit, resets)}</span>
              {!s.unlimited && (
                <span className="flex gap-1" aria-hidden>
                  {Array.from({ length: Math.min(s.limit, 20) }).map((_, i) => (
                    <span key={i} className={`h-2 w-2 rounded-full ${i < s.used ? "bg-violet-500" : "bg-muted-foreground/25"}`} />
                  ))}
                </span>
              )}
            </div>
            {limitReached && <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="ai-limit">{t.limitReached(s.limit, resets)}</p>}
            {tooLarge && <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="ai-too-large">{t.tooLarge}</p>}
          </>
        )}

        {s && s.enabled && (
          <div className={`space-y-3 ${locked ? "pointer-events-none select-none opacity-50" : ""}`} aria-disabled={locked || undefined}>
            <div className="space-y-1">
              <Textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value.slice(0, max))}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void submit(); } }}
                placeholder={t.placeholder}
                rows={3}
                disabled={blocked || running}
                data-testid="ai-instruction"
                autoFocus={!blocked}
              />
              <div className="text-right text-[11px] text-muted-foreground">{t.count(instruction.length, max)}</div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">{t.ideas}</p>
              <div className="flex flex-wrap gap-1.5" data-testid="ai-chips">
                {chips.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    disabled={blocked || running}
                    onClick={() => setInstruction(c.text.slice(0, max))}
                    className="rounded-full border bg-background px-2.5 py-1 text-xs transition hover:border-violet-400 hover:bg-violet-50 disabled:opacity-50 dark:hover:bg-violet-950/40"
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <ul className="space-y-1.5 text-xs text-muted-foreground" data-testid="ai-notes">
          <li className="flex gap-2"><Flag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> <span data-testid="ai-note-brazil">{t.brazil}</span></li>
          {s && <li className="flex gap-2"><Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600" /> <span data-testid="ai-note-expiry">{t.private(s.retentionDays)}</span></li>}
          <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" /> <span>{t.privacy}</span></li>
          {!locked && <li className="flex gap-2"><Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" /> <span>{t.charge}</span></li>}
        </ul>

        {running && (
          <div className="flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 p-3 dark:border-violet-900 dark:bg-violet-950/30" data-testid="ai-running" role="status">
            <Mascot mood="writing" size={44} />
            <div className="space-y-1 text-sm">
              <p className="flex items-center gap-2 font-medium"><Loader2 className="h-4 w-4 animate-spin" /> {t.running}</p>
              <p className="text-xs text-muted-foreground">{t.runningLeave}</p>
            </div>
          </div>
        )}
        {error && !running && <p className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm" data-testid="ai-error" role="alert">{error}</p>}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ai-cancel">{running ? t.close : t.cancel}</Button>
          {!locked && (
            <Button className="gap-2 bg-violet-600 text-white hover:bg-violet-700" onClick={() => void submit()} disabled={blocked || running || !text} data-testid="ai-submit">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {t.submit}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
